/**
 * The add-account dialog footer's live cost preview (frame 86:2): "N créditos/dia"
 * left of the submit button, priced through the no-write estimate route
 * (`useNewAccountEstimate`) rather than computed here, so it always matches what registering
 * would actually record against the tenant's catalogue (§6.6).
 *
 * Pending and errored both render nothing, never the previous figure: `useNewAccountEstimate`
 * carries no `placeholderData`, so a profile switch clears `query.data` back to `undefined` while
 * the new price is in flight, and this reads that absence directly rather than tracking a second
 * "was it ever loaded" flag.
 */
import type { ReactNode } from 'react';
import { Coins } from 'lucide-react';

import { getLocale } from '../../../i18n';
import { credits } from '../../../i18n/format';
import { useNewAccountEstimate } from './api/queries';
import { cpt } from './costPreviewMessages';

export function CostPreview(props: { campaignId: string; profileId: string | null }): ReactNode {
  const locale = getLocale();
  const query = useNewAccountEstimate(
    props.campaignId,
    props.profileId !== null ? { profile_id: props.profileId } : {},
  );
  if (query.data === undefined) return null;
  return (
    <span
      data-testid="cost-preview"
      className="mr-auto flex items-center gap-1.5 text-sm font-medium text-foreground"
    >
      <Coins aria-hidden className="size-3.5 text-muted-foreground" />
      {query.data.credits_per_day === null
        ? cpt('unpriced')
        : cpt('perDay', { credits: credits(query.data.credits_per_day, locale) })}
    </span>
  );
}
